import { createMemo, createSignal, For, Show } from "solid-js";
import { Portal } from "solid-js/web";
import {
  construireArbre,
  construireArbreFilieres,
  elaguerParTexte,
  intitulePlan,
  niveauBioagresseur,
  niveauCulture,
  type Branche,
} from "../domaine/arbres";
import { normaliser } from "../domaine/filtres";
import { etatTemporel, LIBELLES_ETAT } from "../domaine/portefeuille";
import type { Projet } from "../domaine/types";
import { formaterPeriode, pluriel } from "../format";
import { ecrireDansAdresse, lireDansAdresse } from "../navigation";
import { couleurClasse, couleursCibles, fondPastille, type Analyse } from "./analyse";
import { ListeBioagresseurs } from "./Etiquettes";
import { declarerFigure } from "./Figure";
import { Icone } from "./Icone";

const ARBRES = [
  {
    id: "cultures",
    libelle: "Cultures › bioagresseurs › projets",
    niveaux: ["culture", "bioagresseur"],
    lecture: "Culture et bioagresseur sont liés quand un même projet cite les deux.",
  },
  {
    id: "filieres",
    libelle: "Filières › plans d'action › projets",
    niveaux: ["filiere", "plan_action"],
    lecture: "Plan d'action rangé sous la filière de son préfixe (GC, OM…).",
  },
] as const;
type IdArbre = (typeof ARBRES)[number]["id"];
const estArbre = (v: string | null): v is IdArbre => ARBRES.some((a) => a.id === v);

/**
 * Lire le portefeuille en arbre : on déroule une culture pour voir les
 * bioagresseurs qui lui sont associés, puis chaque bioagresseur pour ses projets.
 * Même lecture par filière et plan d'action. Chaque projet porte un résumé ; un
 * clic ouvre sa fiche dans le panneau.
 */
export function VueArbres(props: { analyse: Analyse; outils?: HTMLElement | undefined }) {
  const a = () => props.analyse;
  const p = () => a().portefeuille;
  const lu = lireDansAdresse("arbre");
  const [arbre, setArbre] = createSignal<IdArbre>(estArbre(lu) ? lu : "cultures");
  const choisirArbre = (id: IdArbre) => {
    setArbre(id);
    setOuverts(new Set<string>());
    ecrireDansAdresse("arbre", id === "cultures" ? null : id);
  };
  const definition = () => ARBRES.find((x) => x.id === arbre()) ?? ARBRES[0];

  const complet = createMemo<Branche[]>(() =>
    arbre() === "cultures"
      ? construireArbre(
          p().projets,
          [niveauCulture, niveauBioagresseur(p().classesBioagresseur)],
          ["Culture non renseignée", "Bioagresseur non renseigné"],
        )
      : construireArbreFilieres(p().projets),
  );
  // La recherche élague l'arbre et le déplie. Elle se lit ligne par ligne : une
  // branche reste si le terme est dans son libellé (ou au-dessus), un projet s'il
  // est sur sa propre ligne (acronyme, titre, porteur, bioagresseurs).
  const termes = createMemo(() => normaliser(a().recherche).split(/\s+/).filter(Boolean));
  const texteLigne = (i: number) => {
    const pr = p().projets[i];
    return pr
      ? normaliser([pr.acronyme, pr.titre, pr.porteur, ...pr.bioagresseursCommuns].join(" "))
      : "";
  };
  const affiche = createMemo(() =>
    termes().length ? elaguerParTexte(complet(), termes(), texteLigne) : complet(),
  );
  const enRecherche = () => termes().length > 0;

  const [ouverts, setOuverts] = createSignal<ReadonlySet<string>>(new Set());
  const estOuvert = (chemin: string) => enRecherche() || ouverts().has(chemin);
  const basculer = (chemin: string) => {
    const suivants = new Set(ouverts());
    if (suivants.has(chemin)) suivants.delete(chemin);
    else suivants.add(chemin);
    setOuverts(suivants);
  };
  const toutDeplier = () => {
    const chemins = new Set<string>();
    const parcourir = (branches: Branche[], parent: string) => {
      for (const b of branches) {
        const chemin = `${parent}/${b.cle}`;
        chemins.add(chemin);
        parcourir(b.enfants, chemin);
      }
    };
    parcourir(affiche(), "");
    setOuverts(chemins);
  };

  declarerFigure({
    id: "arbre",
    tableau: () => {
      const [n1, n2] = definition().niveaux;
      return {
        colonnes: [n1, n2, "acronyme", "titre"],
        lignes: complet().flatMap((b1) =>
          b1.enfants.flatMap((b2) =>
            b2.projets.map((i) => [
              b1.libelle,
              b2.libelle,
              p().projets[i]?.acronyme ?? "",
              p().projets[i]?.titre ?? "",
            ]),
          ),
        ),
      };
    },
  });

  const projet = (i: number) => p().projets[i];

  // Comme le graphe : les réglages en tête de zone, fixes ; seul l'arbre défile.
  return (
    <>
      <div class="tete-vue arbre-tete">
        <div class="onglets onglets-petits" role="group" aria-label="Arbre affiché">
          <For each={ARBRES}>
            {(x) => (
              <button
                type="button"
                class="onglet"
                classList={{ actif: arbre() === x.id }}
                aria-pressed={arbre() === x.id}
                onClick={() => choisirArbre(x.id)}
              >
                {x.libelle}
              </button>
            )}
          </For>
        </div>
      </div>
      {/* Déplier / replier rejoignent Exporter dans le coin, au même pas que Recadrer. */}
      <Show when={props.outils}>
        {(cible) => (
          <Portal mount={cible()}>
            <button
              type="button"
              class="bouton bouton-outil"
              disabled={enRecherche()}
              onClick={toutDeplier}
            >
              <Icone nom="deplier" />
              Tout déplier
            </button>
            <button
              type="button"
              class="bouton bouton-outil"
              disabled={enRecherche()}
              onClick={() => setOuverts(new Set<string>())}
            >
              <Icone nom="replier" />
              Tout replier
            </button>
          </Portal>
        )}
      </Show>
      <div class="vue-cadre vue-arbres">
        <p class="explication discret">
          {pluriel(affiche().length, arbre() === "cultures" ? "culture" : "filière")}
          {enRecherche() ? " avec les projets trouvés" : ""}. {definition().lecture}
        </p>

        <Show
          when={affiche().length}
          fallback={<p class="explication">Aucun projet trouvé dans cet arbre.</p>}
        >
          <ul class="arbre" aria-label={definition().libelle}>
            <For each={affiche()}>
              {(b1) => {
                const c1 = `/${b1.cle}`;
                return (
                  <li class="arbre-noeud">
                    <Entree
                      libelle={b1.libelle}
                      projets={b1.projets.length}
                      ouvert={estOuvert(c1)}
                      attenue={b1.nonRenseignee ?? false}
                      surBascule={() => basculer(c1)}
                    />
                    <Show when={estOuvert(c1)}>
                      <ul class="arbre-niveau">
                        <For each={b1.enfants}>
                          {(b2) => {
                            const c2 = `${c1}/${b2.cle}`;
                            return (
                              <li class="arbre-noeud">
                                <Entree
                                  libelle={
                                    arbre() === "filieres" ? intitulePlan(b2.libelle) : b2.libelle
                                  }
                                  titre={b2.libelle}
                                  projets={b2.projets.length}
                                  ouvert={estOuvert(c2)}
                                  attenue={b2.nonRenseignee ?? false}
                                  couleur={
                                    arbre() === "cultures" && !b2.nonRenseignee
                                      ? couleurClasse(p(), b2.cle)
                                      : undefined
                                  }
                                  surBascule={() => basculer(c2)}
                                />
                                <Show when={estOuvert(c2)}>
                                  <ul class="arbre-niveau arbre-projets">
                                    <For each={b2.projets}>
                                      {(i) => (
                                        <Show when={projet(i)}>
                                          {(pr) => (
                                            <ResumeProjet
                                              projet={pr()}
                                              analyse={a()}
                                              trouve={a().misEnAvant?.has(i) ?? false}
                                            />
                                          )}
                                        </Show>
                                      )}
                                    </For>
                                  </ul>
                                </Show>
                              </li>
                            );
                          }}
                        </For>
                      </ul>
                    </Show>
                  </li>
                );
              }}
            </For>
          </ul>
        </Show>
      </div>
    </>
  );
}

function Entree(props: {
  libelle: string;
  titre?: string;
  projets: number;
  ouvert: boolean;
  attenue: boolean;
  couleur?: string;
  surBascule: () => void;
}) {
  return (
    <button
      type="button"
      class="arbre-entree"
      classList={{ attenuee: props.attenue }}
      aria-expanded={props.ouvert}
      title={props.titre}
      onClick={() => props.surBascule()}
    >
      <span class="arbre-chevron" aria-hidden="true">
        {props.ouvert ? "▾" : "▸"}
      </span>
      <Show when={props.couleur}>
        <span class="pastille" style={{ background: props.couleur }} />
      </Show>
      <span class="arbre-libelle">{props.libelle}</span>
      <span class="valeur-secondaire">{pluriel(props.projets, "projet")}</span>
    </button>
  );
}

/** Le mini-résumé d'un projet : de quoi le reconnaître sans ouvrir sa fiche. */
/**
 * Une ligne de projet : pastilles du bioagresseur visé, acronyme (ouvre la fiche),
 * titre ; puis porteur, période, avancement et leviers ; puis les bioagresseurs en
 * étiquettes, groupe par groupe.
 */
export function ResumeProjet(props: { projet: Projet; analyse: Analyse; trouve: boolean }) {
  const pr = () => props.projet;
  const etat = () => etatTemporel(pr(), props.analyse.aujourdhui);
  const familles = () => props.analyse.portefeuille.leviers.length;
  return (
    <li
      class="resume-projet"
      classList={{
        choisi: props.analyse.selection === pr().ref,
        trouve: props.trouve,
      }}
    >
      <div class="resume-tete">
        <Show when={couleursCibles(props.analyse.portefeuille, pr())}>
          {(c) => (
            <Show when={c().length}>
              <span
                class="pastille resume-pastille"
                aria-hidden="true"
                style={{ background: fondPastille(c()) }}
              />
            </Show>
          )}
        </Show>
        <button
          type="button"
          class="lien-projet resume-acronyme"
          onClick={() =>
            props.analyse.selectionner(props.analyse.selection === pr().ref ? null : pr().ref)
          }
        >
          {pr().acronyme}
        </button>
        <span class="resume-titre">{pr().titre}</span>
      </div>
      <div class="resume-faits">
        <Show when={pr().porteur}>
          <span>{pr().porteur}</span>
        </Show>
        <span>{formaterPeriode(pr())}</span>
        <span class={`resume-etat resume-etat-${etat()}`}>{LIBELLES_ETAT[etat()]}</span>
        <span>
          {pr().leviers.length}/{familles()} familles de leviers
        </span>
      </div>
      <Show when={pr().bioagresseursCommuns.length}>
        <ListeBioagresseurs elements={pr().bioagresseursCommuns} />
      </Show>
    </li>
  );
}
